import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { notificationsApi, AgentNotification } from '@/services/api/agentApi';
import { ensureOptionalAgentServiceAvailable } from '@/services/api/agentServiceAvailability';
import { useOrganizationStore } from '@/stores/organization/organization-store';
import { socketClient } from '@/services/websocket/socketClient';
import * as errorUtils from '@/utils/errors';

interface NotificationState {
  notifications: AgentNotification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;

  fetchNotifications: (limit?: number) => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markDismissed: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  addNotification: (notification: AgentNotification) => void;
}

const NOTIFICATION_CREATED_EVENT = 'notification:created';
const NOTIFICATION_REFRESH_DEBOUNCE_MS = 500;

let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let notificationListenerCleanup: (() => void) | null = null;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isNotificationChannel = (value: unknown): value is AgentNotification['channel'] =>
  value === 'in_app' || value === 'push' || value === 'email' || value === 'sms' || value === 'chat';

const isNotificationUrgency = (value: unknown): value is AgentNotification['urgency'] =>
  value === 'critical' || value === 'high' || value === 'normal' || value === 'low';

const isNotificationStatus = (value: unknown): value is AgentNotification['status'] =>
  value === 'pending' ||
  value === 'delivered' ||
  value === 'read' ||
  value === 'dismissed' ||
  value === 'expired';

const isAgentNotification = (value: unknown): value is AgentNotification =>
  isRecord(value) &&
  typeof value.notification_id === 'string' &&
  typeof value.user_id === 'string' &&
  typeof value.organization_id === 'string' &&
  typeof value.title === 'string' &&
  typeof value.body === 'string' &&
  isNotificationChannel(value.channel) &&
  isNotificationUrgency(value.urgency) &&
  isNotificationStatus(value.status) &&
  typeof value.source_agent === 'string' &&
  typeof value.created_at === 'string';

const isUnreadNotification = (notification: AgentNotification): boolean =>
  notification.status === 'pending' || notification.status === 'delivered';

const getActiveOrganizationId = (): string | null => {
  const { currentContext } = useOrganizationStore.getState();

  if (currentContext?.type !== 'organization') {
    return null;
  }

  return currentContext.id.trim().length > 0 ? currentContext.id : null;
};

const isNotificationInActiveOrganization = (
  notification: AgentNotification,
): boolean => {
  const activeOrganizationId = getActiveOrganizationId();
  return activeOrganizationId !== null && notification.organization_id === activeOrganizationId;
};

const scheduleNotificationRefresh = (): void => {
  if (!getActiveOrganizationId()) {
    return;
  }

  if (refreshTimer) {
    return;
  }

  refreshTimer = setTimeout(() => {
    refreshTimer = null;
    void useNotificationStore.getState().fetchNotifications(20);
  }, NOTIFICATION_REFRESH_DEBOUNCE_MS);
};

const handleNotificationSocketReady = (): void => {
  scheduleNotificationRefresh();
};

const handleNotificationCreated = (payload: unknown): void => {
  if (!isRecord(payload)) {
    return;
  }

  const notification = payload.notification;
  if (isAgentNotification(notification) && isNotificationInActiveOrganization(notification)) {
    useNotificationStore.getState().addNotification(notification);
    return;
  }

  if (typeof payload.notificationId === 'string') {
    scheduleNotificationRefresh();
  }
};

export const useNotificationStore = create<NotificationState>()(
  immer((set) => ({
    notifications: [],
    unreadCount: 0,
    isLoading: false,
    error: null,

    fetchNotifications: async (limit = 20) => {
      set((s) => { s.isLoading = true; s.error = null; });
      try {
        if (!(await ensureOptionalAgentServiceAvailable())) {
          set((s) => { s.isLoading = false; });
          return;
        }

        const res = await notificationsApi.list({ limit: String(limit) });
        set((s) => {
          s.notifications = res.data.notifications;
          s.unreadCount = res.data.unreadCount;
          s.isLoading = false;
        });
      } catch (err: unknown) {
        set((s) => { s.isLoading = false; s.error = errorUtils.getErrorMessageOrFallback(err, 'Failed to load notifications'); });
      }
    },

    fetchUnreadCount: async () => {
      try {
        if (!(await ensureOptionalAgentServiceAvailable())) {
          return;
        }

        const res = await notificationsApi.getUnreadCount();
        set((s) => { s.unreadCount = res.data.count; });
      } catch {
        // Silently fail — unread count is non-critical
      }
    },

    markRead: async (id: string) => {
      try {
        await notificationsApi.markRead(id);
        set((s) => {
          const n = s.notifications.find(n => n.notification_id === id);
          const wasUnread = n ? isUnreadNotification(n) : false;
          if (n) {
            n.status = 'read';
            n.read_at = new Date().toISOString();
          }
          if (wasUnread) {
            s.unreadCount = Math.max(0, s.unreadCount - 1);
          }
        });
      } catch {
        // Silently fail
      }
    },

    markDismissed: async (id: string) => {
      try {
        await notificationsApi.markDismissed(id);
        set((s) => {
          const idx = s.notifications.findIndex(n => n.notification_id === id);
          const wasUnread = idx >= 0 ? isUnreadNotification(s.notifications[idx]) : false;

          if (idx >= 0) s.notifications.splice(idx, 1);
          if (wasUnread) {
            s.unreadCount = Math.max(0, s.unreadCount - 1);
          }
        });
      } catch {
        // Silently fail
      }
    },

    markAllRead: async () => {
      try {
        await notificationsApi.markAllRead();
        set((s) => {
          s.notifications.forEach(n => {
            if (n.status === 'pending' || n.status === 'delivered') {
              n.status = 'read';
              n.read_at = new Date().toISOString();
            }
          });
          s.unreadCount = 0;
        });
      } catch {
        // Silently fail
      }
    },

    addNotification: (notification: AgentNotification) => {
      set((s) => {
        const existingIndex = s.notifications.findIndex(
          (item) => item.notification_id === notification.notification_id
        );
        const wasUnread = existingIndex >= 0
          ? isUnreadNotification(s.notifications[existingIndex])
          : false;
        const isUnread = isUnreadNotification(notification);

        if (existingIndex >= 0) {
          s.notifications.splice(existingIndex, 1);
        }

        s.notifications.unshift(notification);
        if (s.notifications.length > 50) s.notifications.pop();

        if (existingIndex < 0 && isUnread) {
          s.unreadCount++;
        } else if (existingIndex >= 0 && !wasUnread && isUnread) {
          s.unreadCount++;
        } else if (existingIndex >= 0 && wasUnread && !isUnread) {
          s.unreadCount = Math.max(0, s.unreadCount - 1);
        }
      });
    },
  }))
);

export const setupNotificationWebSocketListeners = (): void => {
  if (notificationListenerCleanup) {
    return;
  }

  const removeNotificationCreatedListener = socketClient.onCustomEvent(
    NOTIFICATION_CREATED_EVENT,
    handleNotificationCreated,
  );
  const removeConnectedListener = socketClient.on(
    'connected',
    handleNotificationSocketReady,
  );
  const removeReconnectedListener = socketClient.on(
    'reconnected',
    handleNotificationSocketReady,
  );

  notificationListenerCleanup = () => {
    removeNotificationCreatedListener();
    removeConnectedListener();
    removeReconnectedListener();
    notificationListenerCleanup = null;
  };

  if (socketClient.isConnected()) {
    handleNotificationSocketReady();
  }
};

export const cleanupNotificationWebSocketListeners = (): void => {
  if (!notificationListenerCleanup) {
    return;
  }

  notificationListenerCleanup();
};

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    cleanupNotificationWebSocketListeners();
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
  });
}
